import { SearchSmIcon } from '@langchain/untitled-ui-icons';

import { ChevronDownIcon } from 'lucide-react';
import React, { useCallback, useId, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useDebounce } from 'use-debounce';

import { Popover, PopoverContent, PopoverTrigger } from '@/components/Popover';
import { TextOverflowTooltip } from '@/components/TextOverflowTooltip';
import { Button, GroupedTabs, Input } from '@/design-system';
import { useStoredResourceTags } from '@/hooks/useStoredResourceTags/useStoredResourceTags';
import { useOrganizationId, useReposInfinite } from '@/hooks/useSwr';
import {
  CommitWithLookupsSchema,
  ListReposQueryParams,
  RepoWithLookupsSchema,
} from '@/types/schema';
import { addQueryParams } from '@/utils/add-query-params';
import { appOrganizationPath } from '@/utils/constants';

import { getPromptDisplayName } from '../utils/Prompts.utils';
import { PromptSelectorTabContent } from './PromptSelectorTabContent';
import { PromptSelectorProps, PromptSelectorTab } from './types';

export const PromptSelector: React.FC<PromptSelectorProps> = ({
  selectedPromptIdentifier,
  prompt,
  onChange,
  tags,
  className,
  fullInfo = false,
  showNewPromptLink = false,
  emptyStateText,
  newPromptText,
  onSelectNewPrompt,
  align,
  side,
  children,
  showCommitSelector = false,
  selectByLatest = false,
  hidePublicPrompts = false,
}) => {
  const [open, setOpen] = useState(false);
  const id = useId();
  const organizationId = useOrganizationId();
  const [search, setSearch] = useState('');
  const [debouncedSearch] = useDebounce(search, 250);
  const [selectedTab, setSelectedTab] = useState(PromptSelectorTab.private);

  const [searchWithoutTags, setSearchWithoutTags] = useState(false);
  const { selectedTags } = useStoredResourceTags();

  const commonRepoParams: ListReposQueryParams = {
    query: debouncedSearch,
    tags,
    sort_field: 'updated_at',
    sort_direction: 'desc',
    with_latest_manifest: fullInfo,
    tag_value_id: searchWithoutTags
      ? undefined
      : selectedTags.map((t) => t.tag_value_id),
  };

  const privateRepos = useReposInfinite({
    ...commonRepoParams,
    is_public: false,
  });

  const publicRepos = useReposInfinite(
    hidePublicPrompts
      ? null
      : {
          ...commonRepoParams,
          is_public: true,
        }
  );

  const currentReposResponse =
    selectedTab === PromptSelectorTab.private ? privateRepos : publicRepos;

  const allCurrentPrivateRepos = useMemo(
    () => privateRepos.data?.flatMap((page) => page.repos) ?? [],
    [privateRepos.data]
  );

  const allCurrentPublicRepos = useMemo(
    () => publicRepos.data?.flatMap((page) => page.repos) ?? [],
    [publicRepos.data]
  );

  const allCurrentRepos =
    selectedTab === PromptSelectorTab.private
      ? allCurrentPrivateRepos
      : allCurrentPublicRepos;

  const hasMore =
    (currentReposResponse.data?.[0]?.total ?? 0) > allCurrentRepos.length;
  const isNextPageLoading =
    currentReposResponse.isValidating &&
    currentReposResponse.size > 1 &&
    !!currentReposResponse.data?.length;

  const selectedCommitHash = selectedPromptIdentifier?.split(':')[1];
  const { last_commit_hash: latestCommitHash } = prompt ?? {};
  const isLatestCommitHash =
    selectedCommitHash && latestCommitHash?.startsWith(selectedCommitHash);

  const newLink = useMemo(
    () =>
      addQueryParams(`/${appOrganizationPath}/${organizationId}/playground`, {
        type: 'structured',
      }),
    [organizationId]
  );

  const handlePromptSelect = useCallback(
    (prompt: RepoWithLookupsSchema, commit?: CommitWithLookupsSchema) => {
      if (fullInfo) {
        (
          onChange as (
            prompt: RepoWithLookupsSchema,
            commit?: CommitWithLookupsSchema
          ) => void
        )(prompt, commit);
      } else {
        (onChange as (prompt: string) => void)(
          `${getPromptDisplayName(
            prompt.is_public,
            prompt.full_name,
            prompt.repo_handle,
            isLatestCommitHash && selectByLatest
              ? 'latest'
              : prompt.last_commit_hash ?? undefined
          )}`
        );
      }
      setOpen(false);
    },
    [fullInfo, onChange, isLatestCommitHash, selectByLatest]
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        {children ?? (
          <Button
            id={id}
            rightDecorator={ChevronDownIcon}
            className={className}
            size="sm"
            color="secondary"
            tagText={isLatestCommitHash ? 'latest' : selectedCommitHash}
          >
            <TextOverflowTooltip
              className="flex-1"
              textProps={{ as: 'span', variant: 'sm', weight: 'normal' }}
            >
              {selectedPromptIdentifier
                ? selectedPromptIdentifier.split(':')[0]
                : 'Select prompt'}
            </TextOverflowTooltip>
          </Button>
        )}
      </PopoverTrigger>
      <PopoverContent
        align={align ?? 'start'}
        side={side}
        className="min-w-[375px] p-2"
      >
        <div className="flex flex-col gap-2">
          <div className="flex items-center justify-between">
            <label className="text-sm font-medium leading-tight">
              Select a prompt
            </label>

            {showNewPromptLink &&
              (onSelectNewPrompt ? (
                <button
                  type="button"
                  onClick={onSelectNewPrompt}
                  className="cursor-pointer rounded-md border border-secondary px-2 py-1 text-xs outline-none transition-all hover:bg-secondary-hover active:bg-secondary"
                >
                  New
                </button>
              ) : (
                <Link
                  to={newLink}
                  className="rounded-md border border-secondary px-2 py-1 text-xs outline-none transition-all hover:bg-secondary-hover active:bg-secondary"
                >
                  New
                </Link>
              ))}
          </div>

          <Input
            value={search}
            onChange={(value) => setSearch(value)}
            placeholder="Prompt name"
            rightDecorator={<SearchSmIcon className="h-4 w-4" />}
            size="sm"
          />
          {!hidePublicPrompts && (
            <GroupedTabs
              value={selectedTab}
              onChange={(value) => setSelectedTab(value as PromptSelectorTab)}
              size="md"
              className="w-full"
              options={[
                {
                  value: PromptSelectorTab.private,
                  display: 'Private',
                  className: 'w-[50%] justify-center',
                },
                {
                  value: PromptSelectorTab.public,
                  display: 'Public',
                  className: 'w-[50%] justify-center',
                },
              ]}
            />
          )}
          <PromptSelectorTabContent
            selectedPromptIdentifier={selectedPromptIdentifier}
            selectedCommitHash={selectedCommitHash}
            repos={allCurrentRepos}
            isLoading={currentReposResponse.isValidating}
            error={currentReposResponse.error}
            hasMore={hasMore}
            isNextPageLoading={isNextPageLoading}
            setSize={currentReposResponse.setSize}
            showNewPromptLink={showNewPromptLink}
            emptyStateText={emptyStateText}
            newPromptText={newPromptText}
            newLink={newLink}
            handlePromptSelect={handlePromptSelect}
            showCommitSelector={showCommitSelector}
            searchWithoutTags={searchWithoutTags}
            setSearchWithoutTags={setSearchWithoutTags}
          />
        </div>
      </PopoverContent>
    </Popover>
  );
};
